import { create } from 'zustand'
import { apiFetch } from '@/lib/api'
import type { Comment, Notification, Message, Conversation, Profile } from '@/types'

// ---- State & Actions ----

interface SocialState {
  comments: Comment[]
  notifications: Notification[]
  unreadCount: number
  messages: Message[]
  conversations: Conversation[]
  followers: Profile[]
  following: Profile[]
  isLoading: boolean
}

interface SocialActions {
  // Comments
  fetchComments: (articleId: string) => Promise<void>
  addComment: (articleId: string, content: string, gifUrl?: string, gifId?: string, parentId?: string) => Promise<void>
  likeComment: (id: string) => Promise<void>
  deleteComment: (id: string) => Promise<void>
  reuseCaption: (commentId: string, articleId: string) => Promise<void>

  // Notifications
  fetchNotifications: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>

  // Messages
  fetchConversations: () => Promise<void>
  fetchMessages: (userId: string) => Promise<void>
  sendMessage: (userId: string, content: string) => Promise<void>

  // Follow system
  followUser: (userId: string) => Promise<void>
  unfollowUser: (userId: string) => Promise<void>
  fetchFollowers: (userId: string) => Promise<void>
  fetchFollowing: (userId: string) => Promise<void>
}

type SocialStore = SocialState & SocialActions

// ---- Store ----

export const useSocialStore = create<SocialStore>((set, _get) => ({
  // State
  comments: [],
  notifications: [],
  unreadCount: 0,
  messages: [],
  conversations: [],
  followers: [],
  following: [],
  isLoading: false,

  // ================================================================
  // COMMENTS
  // ================================================================

  fetchComments: async (articleId: string) => {
    set({ isLoading: true })
    try {
      const data = await apiFetch<{ comments: Comment[]; total: number }>(`/social/comments/${articleId}`)
      set({ comments: data.comments ?? [] })
    } catch (error) {
      console.error('Fetch comments failed:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  addComment: async (articleId: string, content: string, gifUrl?: string, gifId?: string, parentId?: string) => {
    try {
      const { comment: newComment } = await apiFetch<{ comment: Comment }>('/social/comments', {
        method: 'POST',
        body: {
          article_id: articleId,
          content,
          gif_url: gifUrl ?? null,
          gif_id: gifId ?? null,
          parent_id: parentId ?? null,
        },
      })

      set((state) => {
        if (parentId) {
          // Add as reply to parent comment
          return {
            comments: state.comments.map((c) =>
              c.id === parentId
                ? { ...c, replies: [...c.replies, newComment] }
                : c
            ),
          }
        }
        // Add as top-level comment
        return { comments: [newComment, ...state.comments] }
      })
    } catch (error) {
      console.error('Add comment failed:', error)
      throw error
    }
  },

  likeComment: async (id: string) => {
    try {
      const result = await apiFetch<{ liked: boolean; message?: string }>(
        `/social/comments/${id}/like`,
        { method: 'POST' },
      )

      const isLiked = result.liked
      const delta = isLiked ? 1 : -1

      set((state) => ({
        comments: state.comments.map((c) => {
          if (c.id === id) {
            return { ...c, is_liked: isLiked, likes_count: c.likes_count + delta }
          }
          // Check in replies
          return {
            ...c,
            replies: c.replies.map((r) =>
              r.id === id ? { ...r, is_liked: isLiked, likes_count: r.likes_count + delta } : r
            ),
          }
        }),
      }))
    } catch (error) {
      console.error('Like comment failed:', error)
      throw error
    }
  },

  deleteComment: async (id: string) => {
    try {
      await apiFetch(`/social/comments/${id}`, { method: 'DELETE' })

      set((state) => ({
        comments: state.comments
          .map((c) => {
            if (c.id === id) return { ...c, is_deleted: true }
            return {
              ...c,
              replies: c.replies.map((r) => (r.id === id ? { ...r, is_deleted: true } : r)),
            }
          })
          .filter((c) => !c.is_deleted),
      }))
    } catch (error) {
      console.error('Delete comment failed:', error)
      throw error
    }
  },

  reuseCaption: async (commentId: string, articleId: string) => {
    try {
      await apiFetch(`/social/comments/${commentId}/reuse`, {
        method: 'POST',
        body: { article_id: articleId },
      })
    } catch (error) {
      console.error('Reuse caption failed:', error)
      throw error
    }
  },

  // ================================================================
  // NOTIFICATIONS
  // ================================================================

  fetchNotifications: async () => {
    try {
      const data = await apiFetch<{ notifications: Notification[]; total: number }>('/social/notifications')
      const notifications = data.notifications ?? []
      const unreadCount = notifications.filter((n) => !n.is_read).length
      set({ notifications, unreadCount })
    } catch (error) {
      console.error('Fetch notifications failed:', error)
    }
  },

  markRead: async (id: string) => {
    try {
      await apiFetch(`/social/notifications/${id}/read`, { method: 'PUT' })

      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (error) {
      console.error('Mark read failed:', error)
    }
  },

  markAllRead: async () => {
    try {
      await apiFetch('/social/notifications/read-all', { method: 'PUT' })

      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0,
      }))
    } catch (error) {
      console.error('Mark all read failed:', error)
    }
  },

  // ================================================================
  // MESSAGES
  // ================================================================

  fetchConversations: async () => {
    try {
      const data = await apiFetch<{ conversations: Conversation[] }>('/social/messages/conversations')
      set({ conversations: data.conversations ?? [] })
    } catch (error) {
      console.error('Fetch conversations failed:', error)
    }
  },

  fetchMessages: async (userId: string) => {
    try {
      const data = await apiFetch<{ messages: Message[] }>(`/social/messages/${userId}`)
      set({ messages: data.messages ?? [] })
    } catch (error) {
      console.error('Fetch messages failed:', error)
    }
  },

  sendMessage: async (userId: string, content: string) => {
    try {
      const { message: newMessage } = await apiFetch<{ message: Message }>('/social/messages', {
        method: 'POST',
        body: { receiver_id: userId, content },
      })

      set((state) => ({
        messages: [...state.messages, newMessage],
      }))
    } catch (error) {
      console.error('Send message failed:', error)
      throw error
    }
  },

  // ================================================================
  // FOLLOW SYSTEM
  // ================================================================

  followUser: async (userId: string) => {
    try {
      await apiFetch(`/social/follow/${userId}`, { method: 'POST' })
    } catch (error) {
      console.error('Follow user failed:', error)
      throw error
    }
  },

  unfollowUser: async (userId: string) => {
    try {
      await apiFetch(`/social/follow/${userId}`, { method: 'DELETE' })
    } catch (error) {
      console.error('Unfollow user failed:', error)
      throw error
    }
  },

  fetchFollowers: async (userId: string) => {
    try {
      const data = await apiFetch<{ followers: Profile[]; total: number }>(`/social/followers/${userId}`)
      set({ followers: data.followers ?? [] })
    } catch (error) {
      console.error('Fetch followers failed:', error)
    }
  },

  fetchFollowing: async (userId: string) => {
    try {
      const data = await apiFetch<{ following: Profile[]; total: number }>(`/social/following/${userId}`)
      set({ following: data.following ?? [] })
    } catch (error) {
      console.error('Fetch following failed:', error)
    }
  },
}))
